import { ApolloClient, NormalizedCacheObject } from 'apollo-boost'

import {
  CustomerReturn,
  CustomerReturnStatus,
} from '../../../../generated/graphql'
import { CustomerReturns as QUERY } from '../../../../graphql/Query/Account/CustomerReturns.graphql'

import { ClientBehaviours } from '../../../utils'
import { MockDataReturns } from '../../../data/Returns'

enum ReturnsTypes {
  ACTIVE_RETURNS = 'ACTIVE_RETURNS',
  COMPLETED_RETURNS = 'COMPLETED_RETURNS',
}

export const formatTimestamp = (date: number) => {
  return new Date(date / 1000)
}

const filterActiveAndCompletedReturns = (
  data: CustomerReturn[],
  status: string,
) => {
  return data.filter((item) =>
    status === ReturnsTypes.ACTIVE_RETURNS
      ? item.customerReturnInfo.customerReturnStatus ===
          CustomerReturnStatus.InTransit ||
        item.customerReturnInfo.customerReturnStatus ===
          CustomerReturnStatus.Packing
      : item.customerReturnInfo.customerReturnStatus ===
          CustomerReturnStatus.Cancelled ||
        item.customerReturnInfo.customerReturnStatus ===
          CustomerReturnStatus.Refunding ||
        item.customerReturnInfo.customerReturnStatus ===
          CustomerReturnStatus.Returned,
  )
}

const formatDataReturns = (returns: CustomerReturn[]) => {
  return returns?.map((item) => {
    return {
      status: item.customerReturnInfo.customerReturnStatus,
      orderNumber: item.customerReturnInfo.returnNumber,
      totalCost: {
        displayValue: item.customerReturnInfo.refundValue.displayValue,
      },
      createdAt: formatTimestamp(item.customerReturnInfo.createdAt),
      completedAt: item.customerReturnInfo.completedAt,
      cancelledAt: item.customerReturnInfo.cancelledAt,
      products: item.customerReturnInfo.customerReturnInfoLines.map(
        (infoLine) => ({
          image: infoLine.product?.images[0]?.largeProduct,
          title: infoLine.product.title,
        }),
      ),
      deliveryDateRange: {
        from: new Date(item.customerReturnInfo.createdAt),
        to: new Date(item.customerReturnInfo.completedAt),
      },
    }
  })
}

export const ReturnsList = (
  client: ApolloClient<NormalizedCacheObject>,
  { behaviours }: { behaviours: ClientBehaviours },
) => async (_, args: { filter: { returnStatus: string } }) => {
  try {
    if (behaviours.override) {
      const mockData = await import('../../../data/Returns').then((mod) => {
        return mod.MOCK_DATA_RETURN as MockDataReturns
      })

      const returns = mockData.customer.customerReturns

      if (args?.filter?.returnStatus === CustomerReturnStatus.InTransit) {
        const listOfReturns = returns.filter(
          (item) =>
            item.customerReturnInfo.customerReturnStatus ===
            CustomerReturnStatus.InTransit,
        )

        return {
          hasReturn: !!listOfReturns.length,
          returnNumber:
            listOfReturns.length === 1
              ? listOfReturns[0].customerReturnInfo.returnNumber
              : undefined,
        }
      }

      return {
        completedReturns: formatDataReturns(
          filterActiveAndCompletedReturns(
            returns,
            ReturnsTypes.COMPLETED_RETURNS,
          ),
        ),
        activeReturns: formatDataReturns(
          filterActiveAndCompletedReturns(returns, ReturnsTypes.ACTIVE_RETURNS),
        ),
      }
    }
    const { data, errors } = await client.query({
      query: QUERY,
      variables: args,
      errorPolicy: 'ignore',
    })

    if (args?.filter?.returnStatus === CustomerReturnStatus.InTransit) {
      const listOfReturns = data.customer.customerReturns.filter(
        (item) =>
          item.customerReturnInfo.customerReturnStatus ===
          CustomerReturnStatus.InTransit,
      )

      return {
        hasReturn: !!listOfReturns.length,
        returnNumber:
          listOfReturns.length === 1
            ? listOfReturns[0].customerReturnInfo.returnNumber
            : undefined,
      }
    }

    return {
      errors,
      completedReturns: formatDataReturns(
        filterActiveAndCompletedReturns(
          data.customer.customerReturns,
          ReturnsTypes.COMPLETED_RETURNS,
        ),
      ),
      activeReturns: formatDataReturns(
        filterActiveAndCompletedReturns(
          data.customer.customerReturns,
          ReturnsTypes.ACTIVE_RETURNS,
        ),
      ),
    }
  } catch {
    throw Error('Network error in Returns.')
  }
}
